import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { CalcPf } from "@/components/accordi/CalcPf";
import { CalcCoopSalute } from "@/components/accordi/CalcCoopSalute";
import { CalcCasa } from "@/components/accordi/CalcCasa";
import { CalcEnte } from "@/components/accordi/CalcEnte";
import { ConsulenzaDialog } from "@/components/accordi/ConsulenzaDialog";
import { SalvaPreventivoDialog } from "@/components/accordi/SalvaPreventivoDialog";
import { useNoIndex, logoSrc, codiceKey } from "@/components/accordi/accordiUtils";
import { toast } from "sonner";
import { ArrowLeft, Building2, ExternalLink, Loader2, LogOut, MessageCircle, ShieldCheck, UserPlus } from "lucide-react";

const ProdottoCard = ({ p, active, onClick }) => (
  <button onClick={onClick} data-testid={`area-prodotto-${p.code}`}
    className={`rounded-2xl border-2 bg-white p-4 text-left transition-all hover:-translate-y-0.5 ${active ? "border-terracotta shadow-md" : "border-border/60"}`}>
    <ShieldCheck className="mb-2 h-5 w-5 text-terracotta" />
    <div className="font-heading font-semibold leading-tight text-forest">{p.label}</div>
    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
      {p.sconto_pct > 0 && <span className="rounded-full bg-terracotta/10 px-2 py-0.5 font-semibold text-terracotta">−{p.sconto_pct.toLocaleString("it-IT")}% riservato</span>}
      {p.consulenza_obbligatoria && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">Con consulenza</span>}
    </div>
  </button>
);

// Area riservata: solo i prodotti abilitati per l'accordo, con sconto applicato automaticamente.
export default function AccordoArea() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const codice = sessionStorage.getItem(codiceKey(slug));
  const [a, setA] = useState(null);
  const [sel, setSel] = useState(null);
  const [cons, setCons] = useState(null);
  const [salva, setSalva] = useState(null);
  const [referral, setReferral] = useState(false);
  useNoIndex();

  useEffect(() => {
    if (!codice) { navigate(`/accordi/${slug}`, { replace: true }); return; }
    const k = `accordo_aa_${slug}`;
    const traccia = !sessionStorage.getItem(k);
    sessionStorage.setItem(k, "1");
    api.post("/public/accordi/accesso", { codice, traccia }).then(({ data }) => { setA(data); setSel(data.prodotti[0]?.code); })
      .catch((e) => { toast.error(apiError(e)); sessionStorage.removeItem(codiceKey(slug)); sessionStorage.removeItem(k); navigate(`/accordi/${slug}`, { replace: true }); });
  }, [slug]); // eslint-disable-line

  if (!a) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const p = a.prodotti.find((x) => x.code === sel);
  const esci = () => { sessionStorage.removeItem(codiceKey(slug)); navigate(`/accordi/${slug}`); };

  return (
    <div className="min-h-screen bg-bone" data-testid="accordo-area">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-5 py-4">
          {a.logo_url ? <img src={logoSrc(a.logo_url)} alt="" className="h-10 w-10 object-contain" /> : <Building2 className="h-9 w-9 text-forest/40" />}
          <div className="min-w-0 flex-1">
            <div className="truncate font-heading text-lg font-semibold text-forest" data-testid="area-accordo-nome">{a.denominazione}</div>
            <div className="truncate text-xs text-muted-foreground">Accordo {a.codice}<span className="hidden sm:inline"> · valido fino al {new Date(a.scadenza).toLocaleDateString("it-IT")}</span></div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReferral(true)} data-testid="area-presenta-collega" className="gap-1.5 border-terracotta/40 text-terracotta"><UserPlus className="h-4 w-4" /><span className="hidden sm:inline">Presenta un collega</span></Button>
          <Button variant="ghost" size="sm" onClick={esci} data-testid="area-esci" className="gap-1.5 text-forest"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Esci</span></Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-8 px-5 py-10">
        <button onClick={() => navigate(`/accordi/${slug}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-forest"><ArrowLeft className="h-4 w-4" />Presentazione dell'accordo</button>
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest sm:text-4xl">Il tuo preventivo riservato</h1>
          <p className="mt-1 text-muted-foreground">Scegli la soluzione: lo sconto dell'accordo è già applicato al premio.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="area-prodotti">
          {a.prodotti.map((x) => <ProdottoCard key={x.code} p={x} active={x.code === sel} onClick={() => setSel(x.code)} />)}
        </div>
        {p && (
          <section className="space-y-4 rounded-2xl border bg-white p-6" data-testid={`area-sezione-${p.code}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-xl font-semibold text-forest">{p.label}</h2>
              <Button variant="outline" size="sm" onClick={() => setCons(p)} data-testid="area-consulenza" className="gap-1.5"><MessageCircle className="h-4 w-4" />Richiedi una consulenza dedicata</Button>
            </div>
            {p.consulenza_obbligatoria && p.tipo !== "consulenza" && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="area-consulenza-obbligatoria">Puoi simulare il premio, ma per procedere serve una consulenza dedicata.</p>}
            {p.tipo === "pf" && <CalcPf codice={a.codice} onSalva={(input) => setSalva({ product: p.code, input })} />}
            {p.tipo === "casa" && <CalcCasa codice={a.codice} onSalva={(input) => setSalva({ product: p.code, input })} onConsulenza={() => setCons(p)} />}
            {p.tipo === "cs" && <CalcCoopSalute codice={a.codice} onSalva={(input) => setSalva({ product: p.code, input })} />}
            {["rc", "inf", "mb", "mrv", "do", "fs", "catnat"].includes(p.tipo) && <CalcEnte key={p.code} codice={a.codice} product={p.code} onSalva={(input, campi) => setSalva({ product: p.code, input, ente: campi.ente ? campi : null })} onConsulenza={() => setCons(p)} />}
            {p.tipo === "link" && (
              <div className="space-y-3 text-sm" data-testid="area-rca">
                <p className="text-muted-foreground">Il preventivo RCA si calcola su «Basta la Targa». Comunica il codice <b className="font-mono text-forest">{a.codice}</b> al consulente: lo sconto riservato{p.sconto_pct > 0 ? ` del ${p.sconto_pct}%` : ""} viene applicato in fase di proposta.</p>
                <a href={`${a.rca_url}?accordo=${encodeURIComponent(a.codice)}`} target="_blank" rel="noopener noreferrer" data-testid="area-rca-link"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-forest px-4 py-2 font-medium text-white hover:bg-forest-hover">Vai a Basta la Targa <ExternalLink className="h-4 w-4" /></a>
              </div>
            )}
            {p.tipo === "consulenza" && <p className="text-sm text-muted-foreground" data-testid="area-solo-consulenza">Questa soluzione si costruisce su misura con un consulente: richiedi una consulenza dedicata e ti ricontattiamo con la proposta riservata all'accordo.</p>}
          </section>
        )}
      </main>
      <ConsulenzaDialog open={!!cons} onOpenChange={(v) => !v && setCons(null)} codice={a.codice} product={cons?.code} productLabel={cons?.label} />
      <ConsulenzaDialog open={referral} onOpenChange={setReferral} codice={a.codice} tipo="referral" />
      {salva && <SalvaPreventivoDialog open onOpenChange={(v) => !v && setSalva(null)} codice={a.codice} {...salva} />}
    </div>
  );
}
